import {
  BILLING_FREQUENCY_DB,
  BILLING_FREQUENCY_LABEL,
  BillingFrequencyType,
  BillingPlanScheduleType,
} from '@/const/shared';
import {
  getEnableVerificationText,
  getOveragePriceText,
  getPaymentsLimitText,
  getPlanPrice,
  getUsersLimitText,
} from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/helpers';
import { PlanCardInfoRow } from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/ui/PlanCardInfoRow/PlanCardInfoRow';
import {
  DashedLine,
  OverageRateBlock,
  PricingPlanCardWrapper,
} from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/ui/PricingPlanCard/styles';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { Button } from '@/components/Button/Button';
import { convertCentsToDollars } from '@/helpers/formatHelpers';
import { BillingPlanType } from '@/api/v1/billing/getBillingPlanDetailsApi';
import { isSingleUsePaymentPlan } from '@/helpers/billingHelpers';

type CardBillingPlanType = {
  name: string;
  order: number;
  scheduleType: BillingPlanScheduleType;
  billingFrequency: Record<BillingFrequencyType, BillingPlanType>;
};

type PricingPlanCardType = {
  plan: CardBillingPlanType;
  billingFrequency: BillingFrequencyType;
  onSelect: (plan: BillingPlanType) => void;
};

export const PricingPlanCard = ({ plan, billingFrequency, onSelect }: PricingPlanCardType) => {
  if (!plan) return null;

  const selectPlan = () => {
    onSelect(planDetails);
  };

  const singleUsePaymentPlan = isSingleUsePaymentPlan(plan.scheduleType);
  // Single payment plan has only yearly billing frequency
  const planBillingFrequency = singleUsePaymentPlan ? BILLING_FREQUENCY_DB.YEAR : billingFrequency;
  const planDetails = plan.billingFrequency[planBillingFrequency];
  const { extra_view_price_amount, max_users, monthly_views, enabled_id_verification, recommended } = planDetails;

  const planPrice = getPlanPrice(planDetails, 0);
  const billedPeriod = `billed ${BILLING_FREQUENCY_LABEL[planBillingFrequency]}`;
  const overagePrice = convertCentsToDollars(extra_view_price_amount, 0);

  return (
    <PricingPlanCardWrapper isSelected={recommended}>
      <Box marginBottom='16px'>
        <Box display='flex' flexDirection='column'>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography color={COLORS.black} variant='headline4' fontWeight='semibold'>
              {plan.name}
            </Typography>
            <Typography variant='headline3' color={COLORS.green[450]} fontWeight='semibold'>
              {planPrice}
            </Typography>
          </Box>
          <Typography color={COLORS.grey[600]} fontWeight='medium' variant='caption' height='16px'>
            {!singleUsePaymentPlan && billedPeriod}
          </Typography>
        </Box>
      </Box>
      <DashedLine />
      <Box display='flex' flexDirection='column' rowGap='8px' paddingTop='20px' paddingBottom='14px'>
        <PlanCardInfoRow>{getPaymentsLimitText(monthly_views)}</PlanCardInfoRow>
        <PlanCardInfoRow>{getUsersLimitText(max_users)}</PlanCardInfoRow>
        <PlanCardInfoRow>{getEnableVerificationText(enabled_id_verification)}</PlanCardInfoRow>
      </Box>
      <OverageRateBlock>{getOveragePriceText(overagePrice)}</OverageRateBlock>
      <Box marginTop='20px'>
        <Button variant='primary' onClick={selectPlan}>
          Select
        </Button>
      </Box>
    </PricingPlanCardWrapper>
  );
};
